import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button, softBadgeColors } from '@structural-climbing/ui'
import { Skeleton } from '../../design/Skeleton'
import { disciplineForSection, gradeDisplay } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import { useGymDisciplines } from '../gym/useGymDisciplines'
import { disciplinePath } from '../nav/navGroups'
import type { GymRoute } from './useGymRoutes'

function isNewRoute(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < 3 * 24 * 60 * 60 * 1000
}

interface Props {
  routes: GymRoute[]
  total: number
  loading: boolean
  gymSlug: string
  apiUrl: string
  /** The heading. Defaults to "New routes in the gym". */
  title?: string
  /** Shown instead of "no routes" when the list is empty on purpose. */
  emptyLabel?: string
  /** `sm` for Home in the gym, where the row sits under the session stats. */
  size?: 'md' | 'sm'
  /**
   * Opens a tapped route in its own sheet, right here. Without it the whole row
   * leads to the wall, where the member then had to find the route again.
   */
  onPick?: (route: GymRoute) => void
}

/** How many tiles the row holds before "see all" takes over. */
export const TEASER_LIMIT = 8

/**
 * A row of route tiles you swipe sideways, leading to the wall.
 *
 * Home uses it twice: the newest routes before check-in, and the ones you have
 * not topped yet while you are inside. It used to be a vertical list of three
 * that took a screen of its own.
 */
export function RoutesTeaser({
  routes,
  total,
  loading,
  gymSlug,
  apiUrl,
  title,
  emptyLabel,
  size = 'md',
  onPick,
}: Props) {
  const wallTo = `/${gymSlug}/${disciplinePath(useGymDisciplines()[0])}/routes`
  const tile = size === 'sm' ? 'w-[5.5rem]' : 'w-28'
  const photo = size === 'sm' ? 'h-[52px]' : 'h-[72px]'
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()

  return (
    <Outer to={onPick ? null : wallTo}>
      <div className="flex items-baseline justify-between gap-2 px-1">
        <p className="eyebrow text-foreground/40">{title ?? t('routes.newRoutesLabel')}</p>
        {!loading &&
          total > 0 &&
          (onPick ? (
            // The tiles open routes here, so only the count leads to the wall.
            <Link to={wallTo} className="shrink-0 text-xs font-semibold text-[var(--gym-primary)]">
              {t('routes.totalRoutesCount', { count: total })} →
            </Link>
          ) : (
            <span className="shrink-0 text-xs font-semibold text-[var(--gym-primary)]">
              {t('routes.totalRoutesCount', { count: total })} →
            </span>
          ))}
      </div>

      {loading ? (
        <div className="flex gap-2.5 overflow-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton
              key={i}
              className={`${size === 'sm' ? 'h-[84px]' : 'h-[112px]'} ${tile} shrink-0 rounded-lg`}
            />
          ))}
        </div>
      ) : routes.length === 0 ? (
        <p className="px-1 text-sm text-foreground/50">{emptyLabel ?? t('routes.noRoutes')}</p>
      ) : (
        <div className="scrollbar-hide -mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1">
          {routes.slice(0, TEASER_LIMIT).map((route) => {
            const grade = gradeDisplay(
              gradeConfig,
              route.grade,
              disciplineForSection(route.section?.discipline),
            )
            const badge = softBadgeColors(grade.color)
            const tileClass = `${tile} shrink-0 overflow-hidden rounded-lg border border-[var(--gym-line)] bg-content1 shadow-e1`
            const body = (
              <>
                {route.image_r2_key ? (
                  <img
                    src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
                    alt={t('routes.gradeBadge', { grade: grade.label })}
                    className={`${photo} w-full object-cover`}
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`flex ${photo} w-full items-center justify-center bg-content2 text-xl text-foreground/30`}
                  >
                    ⛰
                  </div>
                )}
                <div
                  className={`flex items-center gap-1.5 px-2 ${size === 'sm' ? 'py-1.5' : 'py-2'}`}
                >
                  <span
                    className="h-3.5 w-3.5 shrink-0 rounded-full border border-foreground/10"
                    style={{ backgroundColor: route.color ?? '#9ca3af' }}
                  />
                  <span
                    className="rounded px-1.5 py-0.5 text-xs font-semibold"
                    style={{ backgroundColor: badge.bg, color: badge.fg }}
                  >
                    {grade.label}
                  </span>
                  {isNewRoute(route.created_at) && (
                    <span className="rounded bg-success px-1 py-0.5 text-[9px] font-bold uppercase leading-none text-white">
                      {t('routes.new')}
                    </span>
                  )}
                </div>
              </>
            )
            return onPick ? (
              <Button
                key={route.id}
                data-testid="route-tile"
                disableRipple
                variant="light"
                onPress={() => onPick(route)}
                className={`${tileClass} block h-auto min-w-0 p-0 text-left data-[hover=true]:bg-content1`}
              >
                {body}
              </Button>
            ) : (
              <div key={route.id} data-testid="route-tile" className={tileClass}>
                {body}
              </div>
            )
          })}
        </div>
      )}
    </Outer>
  )
}

/** The whole row is one link to the wall, unless its tiles open routes here. */
function Outer({ to, children }: { to: string | null; children: React.ReactNode }) {
  if (to)
    return (
      <Link to={to} className="flex flex-col gap-2">
        {children}
      </Link>
    )
  return <div className="flex flex-col gap-2">{children}</div>
}
